import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CardActions from '@material-ui/core/CardActions';
import Collapse from '@material-ui/core/Collapse';
import CloseIcon from 'assets/images/close-icon.svg';
import DownloadIcon from 'assets/images/download-icon.svg';
import DocumentIcon from 'assets/images/document-icon.svg';
import DownloadIconGr from 'assets/images/download-icon-grey.svg';
import DocumentIconGr from 'assets/images/document-icon-grey.svg';
import RefreshIcon from 'assets/images/refresh-icon.svg';
import { ReactComponent as BusinessIcon } from 'assets/images/ion_business.svg';
import ConfirmDialog from 'components/ConfirmDialog';
import { checkUserRole, getProjectName, hasPermissionsFor } from 'helpers/_helpers';
import { agoDate, clockTime } from 'helpers/_date-helpers';
import SignaturePopover from 'components/SignaturePopover';
import styles from './styles';
import { fetchDeleteSubmittedSheet } from 'store/worklogs';
import { useDispatch, useSelector } from 'react-redux';
import { resendEmail } from './helpers/apiCalls';
import { dateSettingsFormat, getTimeFormat } from 'common/dateFormatConfig';
import ArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import ArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import "./swiper.css";


const SubmittedSheetCard = ({ classes, openSnackbar, setForceUpdate, sheet }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dateFormat, timeFormat } = useSelector(store => store.personalProfile.organization.settings);
  const allLocations = useSelector(store => store.personalProfile.activeLocations);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [signatures, setSignatures] = useState([]);

  const swiperRef = useRef(null);
  const swiperPrevRef = useRef(null);
  const swiperNextRef = useRef(null);

  const submissions = useMemo(() => {
    const cloned = structuredClone(sheet.submissions);
    return cloned.sort((a, b) => {
      const aDate = new Date(a.submittedAt || a.createdAt || new Date());
      const bDate = new Date(b.submittedAt || b.createdAt || new Date());
      return bDate.getTime() - aDate.getTime();
    })
  }, [sheet, sheet?.submissions])

  useEffect(() => {
    if (swiperRef?.current?.swiper && submissions?.length > 1) swiperRef.current.swiper.init();
  }, [swiperRef, submissions])
  
  const submission = useMemo(() => {
    if (activeIndex > submissions?.length - 1) return submissions[activeIndex -1]
    return submissions[activeIndex]
  }, [submissions, activeIndex]);

  const handleExpandClick = (e) => {
    const nextExpanded = !isExpanded;
    e.stopPropagation();
    setIsExpanded(nextExpanded);
    setTimeout(() => swiperRef?.current?.swiper?.updateAutoHeight(nextExpanded ? 200 : 100), 20);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  
  const deleteSheet = useCallback(async () => {
    await dispatch(fetchDeleteSubmittedSheet(submission._id));
    openSnackbar('success',' Successfully Deleted!')
    setIsModalOpen(false)
  }, [submission, activeIndex]);

  const openDeleteModal = (e) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const getSheetOwner = useCallback(() => {
    return submission.username
      ? `by ${submission.username}`
      : null;
  }, [submission, activeIndex]);

  const handleOpenPopover = (item) => (event) => {
    event.stopPropagation();
    
    setSignatures(item.signatures);
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null)

    setTimeout(() => {
      setSignatures([]);
    }, 350)
  };

  const isAnySignature = () => {
    const { signatures } = submission;
    return Boolean(signatures && (signatures.dot || signatures.contractor));
  };

  const getEmailFieldMessage = () => {
    // TODO: clarify condition to show spinner
    // <img src='/images/ajax-loader.gif' width='16' height='16' />;
    const { email } = submission;
    return email || 'N/A';
  };

  const materialsCSVIsShow = submission?.materialsCSV.split('\n').length > 1;
  const totalsCSVIsShow = submission?.totalsCSV.split('\n').length > 1;

  const openCSV = (type) => (e) => {
    if (e) e.stopPropagation();
    if (type == 'materials' && !materialsCSVIsShow) return null;
    if (type == 'totals' && !totalsCSVIsShow) return null;
    const url = `/submissions/${submission._id}/csv/${type}`;
    window.open(url, '_blank');
  };

  const openPDF = (e) => {
    if (e) e.stopPropagation();
    if (submission.certPDF) {
      window.open(
        `${process.env.REACT_APP_BASE_URL}/submissions/${submission._id}/certificate?authToken=${localStorage.getItem('authorization-token')}`,
        '_blank'
      );
    }
  };

  const openWorklogPdfReport = (e) => {
    if (e) e.stopPropagation();
    if (submission.pdfReportKey) {
      window.open(
        `/submission/${submission._id}/pdf-report`
      )
    }
  }

  const openDocuSign = (envelopeId) => (e) => {
    e.stopPropagation();
    const url = `${process.env.REACT_APP_BASE_URL}/submissions/${submission._id}/docusign-certificate/${envelopeId}`;
    window.open(url, '_blank');
  };

  const openWorklogDetails = () => {
    navigate(`/worklog/submissions/${sheet._id}`);
  };

  const swipe = (direction) => () => {
    if (swiperRef && swiperRef.current) {
       if (direction === 'prev' && activeIndex > 0) {
         swiperRef.current.swiper.slideTo(activeIndex - 1);
         setActiveIndex(activeIndex - 1)
        } else if (direction === 'next' && activeIndex < submissions?.length -1) {
          swiperRef.current.swiper.slideTo(activeIndex + 1);
          setActiveIndex(activeIndex + 1)
       }
    }
  }

  const hasFullAccess = () => hasPermissionsFor('worklogsFullAccess');

  const submissionTime = (item) => {
    return `${agoDate(
      item.startedAt,
      null,
      dateSettingsFormat(dateFormat, timeFormat)
        .dayDateSettingsFormat
    )} at ${clockTime(
      item.startedAt,
      getTimeFormat([], timeFormat, dateFormat).formatForTimePars
    )}`
  }

  return (
    <>
      <Card className={classes.card} onClick={openWorklogDetails}>
        <Grid container className={classes.paddingBox}>
          {checkUserRole('Field Technician') && (
            <Grid item xs={12}>
              <div className={classes.sheetLocation}>
                <BusinessIcon className={classnames(classes.locationIcon)} />
                {allLocations?.find(({ _id }) => _id === sheet.tenantLocationId)?.name}
              </div>
            </Grid>
          )}
          <Grid item xs={12} className={classes.paddingBottom}>
            <div className={classnames(classes.inlineFlex, classes.flexHeader)}>
              <Typography variant="h6" color="textPrimary" className={classes.fixedWidth}>
                {sheet ? getProjectName(sheet.project) : ''}
              </Typography>
              {hasFullAccess() && (
                <Button
                  color="secondary"
                  className={classes.deleteButton}
                  onClick={openDeleteModal}
                  disableRipple
                >
                  <img src={CloseIcon} alt="delete" />
                </Button>
              )}
            </div>
          </Grid>
          <Grid item xs={12} className={classes.paddingBottom}>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classes.regularFontWeight}
            >
              {sheet
                ? `Created on ${
                    sheet.createdAtDate
                      ? agoDate(
                          sheet.createdAtDate,
                          null,
                          dateSettingsFormat(dateFormat, timeFormat)
                            .dayDateSettingsFormat
                        )
                      : agoDate(
                          sheet.createdAt,
                          null,
                          dateSettingsFormat(dateFormat, timeFormat)
                            .dayDateSettingsFormat
                        )
                  }`
                : null}
            </Typography>
          </Grid>

          <Grid item xs={12} className={classes.paddingBottom}>
            <Divider />
          </Grid>
          
          {submissions?.length > 1 ? (
            <Grid item xs={12} onClick={(e) => e.stopPropagation()} className={classes.paddingBottom}>
              <Typography variant="body1" color="textSecondary" className={classnames(classes.regularFontWeight, classes.paddingBottom)}>
                Submissions: ({submissions.length})
              </Typography>
              <Grid item xs={12} className={classnames(classes.flex, classes.paddingTop)}>
                <ArrowLeftIcon ref={swiperPrevRef} className={classnames(classes.inlineBlock, classes.customPrev, 'swap-prev')} onClick={swipe('prev')} />
                <div className={classes.customPaginationWrapper}>
                  <div id={`custom-pagination__${sheet?._id}`} className={classnames(classes.customPagination, 'custom-pagination')}></div>
                </div>
                <ArrowRightIcon ref={swiperNextRef} className={classnames(classes.inlineBlock, classes.customNext, 'swap-next')} onClick={swipe('next')}/>
              </Grid>
            </Grid>
          ) : null}
        </Grid>
          
        <Swiper
          key={`${sheet._id}_${submissions?.length}`}
          modules={[Navigation, Pagination]}
          ref={swiperRef}
          autoHeight={true}
          navigation={{
            nextEl: swiperNextRef?.current,
            prevEl: swiperNextRef?.current,
          }}
          pagination={{
            el: `#custom-pagination__${sheet?._id}`,
            clickable: true,
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.activeIndex);
          }}
        >
          {submissions.map((item, idx) => (
            <SwiperSlide key={item.id}>
              <Grid container className={classes.paddingBox}>
          

                <Grid item xs={12} md={9} className={classes.paddingBottom}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classnames(classes.regularFontWeight, classes.paddingBottomSmall)}
                  >
                    Submitted:
                  </Typography>
                  <Typography variant="body1" color="textPrimary">
                    {submissionTime(item)}
                  </Typography>
                  {getSheetOwner() && (
                    <Typography variant="body1" color="textPrimary">
                      {getSheetOwner()}
                    </Typography>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                  className={classnames(classes.paddingBottom, classes.paddingLeft)}
                >
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classnames(classes.regularFontWeight, classes.paddingBottomSmall)}
                  >
                    Email:
                  </Typography>
                  <Typography variant="body1" color="textPrimary" className={classes.inlineFlex}>
                    <span className={classes.capitalize}>{getEmailFieldMessage()}</span>
                    {getEmailFieldMessage() !== 'N/A' && (
                      <IconButton
                        className={classes.iconButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          resendEmail(item._id, openSnackbar);
                        }}
                      >
                        <img src={RefreshIcon} alt="refresh" />
                      </IconButton>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} className={classes.paddingBottom}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classnames(classes.regularFontWeight, classes.paddingBottomSmall)}
                  >
                    Type:
                  </Typography>
                  <Typography variant="body1" color="textPrimary">
                    {item.type}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6} className={classes.paddingBottom}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className={classnames(classes.regularFontWeight, classes.paddingBottomSmall)}
                  >
                    Signature:
                  </Typography>
                  <Typography
                    variant="body1"
                    color="textPrimary"
                    onClick={isAnySignature() ? handleOpenPopover(item) : null}
                    className={classnames(isAnySignature() && classes.linkText)}
                  >
                    {isAnySignature() ? 'Show' : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                {/* <Collapse className="slide-collapse" in={isExpanded} unmountOnExit> */}
                  <Grid container style={{display: isExpanded ? 'block' : 'none'}}>
                    <Grid
                      item
                      xs={12}
                      className={classnames(classes.paddingBox, classes.paddingTop, classes.borderTop)}
                    >
                      <div
                        className={materialsCSVIsShow ? classes.linkWrapper : classes.disabledIcon}
                        onClick={openCSV('materials')}
                      >
                        <Typography variant="body1" color="textPrimary" className={classes.inlineFlex}>
                          <span>
                            <IconButton className={classes.iconButton} disableRipple>
                              <img src={DocumentIcon} />
                            </IconButton>
                            <span className={classes.marginLeft}>Materials CSV</span>
                          </span>
                          <IconButton className={classes.iconButton} disableRipple>
                            <img src={DownloadIcon} alt="download" />
                          </IconButton>
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item xs={12} className={classnames(classes.paddingBox)}>
                      <div
                        className={totalsCSVIsShow ? classes.linkWrapper : classes.disabledIcon}
                        onClick={openCSV('totals')}
                      >
                        <Typography variant="body1" color="textPrimary" className={classes.inlineFlex}>
                          <span>
                            <IconButton className={classes.iconButton} disableRipple>
                              <img src={DocumentIcon} />
                            </IconButton>
                            <span className={classes.marginLeft}>Totals CSV</span>
                          </span>
                          <IconButton className={classes.iconButton} disableRipple>
                            <img src={DownloadIcon} alt="download" />
                          </IconButton>
                        </Typography>
                      </div>
                    </Grid>
                    {Boolean(!item.certPDF || item.certPDF !== 'N/A') && (
                      <Grid item xs={12} className={classnames(classes.paddingBox)}>
                        {item.certPDF === 'ERROR' && (
                          <div className={classes.disabledLink}>
                            <Typography
                              variant="body1"
                              color="textPrimary"
                              className={classes.inlineFlex}
                            >
                              <span>
                                <IconButton
                                  className={classnames(classes.iconButton, classes.defaultCursor)}
                                  disableRipple
                                >
                                  <img src={DocumentIconGr} />
                                </IconButton>
                                <span className={classes.marginLeft}>Certificate PDF</span>
                              </span>
                              <IconButton
                                className={classnames(classes.iconButton, classes.defaultCursor)}
                                disableRipple
                              >
                                <img src={DownloadIconGr} alt="download" />
                              </IconButton>
                            </Typography>
                          </div>
                        )}
                        {Boolean(item.certPDF && item.certPDF !== 'ERROR') && (
                          <div className={classes.linkWrapper} onClick={openPDF}>
                            <Typography
                              variant="body1"
                              color="textPrimary"
                              className={classes.inlineFlex}
                            >
                              <span>
                                <IconButton className={classes.iconButton} disableRipple>
                                  <img src={DocumentIcon} alt="document" />
                                </IconButton>
                                <span className={classes.marginLeft}>Certificate PDF</span>
                              </span>
                              <IconButton className={classes.iconButton} disableRipple>
                                <img src={DownloadIcon} alt="download" />
                              </IconButton>
                            </Typography>
                          </div>
                        )}
                      </Grid>
                    )}
                    {item?.docuSign?.envelopeId
                      ? item?.docuSign?.status === 'completed' && (
                          <Grid item xs={12} className={classnames(classes.paddingBox)}>
                            <div
                              className={classes.linkWrapper}
                              onClick={openDocuSign(item.docuSign.envelopeId)}
                            >
                              <Typography
                                variant="body1"
                                color="textPrimary"
                                className={classes.inlineFlex}
                              >
                                <span>
                                  <IconButton className={classes.iconButton} disableRipple>
                                    <img src={DocumentIcon} />
                                  </IconButton>
                                  <span className={classes.marginLeft}>DocuSign Certificate</span>
                                </span>
                                <IconButton className={classes.iconButton} disableRipple>
                                  <img src={DownloadIcon} alt="download" />
                                </IconButton>
                              </Typography>
                            </div>
                          </Grid>
                        )
                      : item?.docuSign?.envelopes?.filter(
                          (envelope) => envelope.status === 'completed'
                        )?.length
                      ? item.docuSign.envelopes
                          .filter((envelope) => envelope.status === 'completed')
                          .map((envelope) => (
                            <Grid item xs={12} className={classnames(classes.paddingBox)}>
                              <div
                                className={classes.linkWrapper}
                                onClick={openDocuSign(envelope.envelopeId)}
                              >
                                <Typography
                                  variant="body1"
                                  color="textPrimary"
                                  className={classes.inlineFlex}
                                >
                                  <span>
                                    <IconButton className={classes.iconButton} disableRipple>
                                      <img src={DocumentIcon} />
                                    </IconButton>
                                    <span className={classes.marginLeft}>
                                      DocuSign Certificate
                                      {envelope.material ? ` - ${envelope.material}` : ''}
                                    </span>
                                  </span>
                                  <IconButton className={classes.iconButton} disableRipple>
                                    <img src={DownloadIcon} alt="download" />
                                  </IconButton>
                                </Typography>
                              </div>
                            </Grid>
                          ))
                      : null}
                    {Boolean(item.pdfReportKey) && (
                      <Grid item xs={12} className={classnames(classes.paddingBox)}>
                        {/^error/.test(item.pdfReportKey) && (
                          <div className={classes.disabledLink}>
                            <Typography
                              variant="body1"
                              color="textPrimary"
                              className={classes.inlineFlex}
                            >
                              <span>
                                <IconButton
                                  className={classnames(classes.iconButton, classes.defaultCursor)}
                                  disableRipple
                                >
                                  <img src={DocumentIconGr} />
                                </IconButton>
                                <span className={classes.marginLeft}>Work Log PDF</span>
                              </span>
                              <IconButton
                                className={classnames(classes.iconButton, classes.defaultCursor)}
                                disableRipple
                              >
                                <img src={DownloadIconGr} alt="download" />
                              </IconButton>
                            </Typography>
                          </div>
                        )}
                        {Boolean(item.pdfReportKey) && !/^error/.test(item.pdfReportKey) && (
                          <div className={classes.linkWrapper} onClick={openWorklogPdfReport}>
                            <Typography
                              variant="body1"
                              color="textPrimary"
                              className={classes.inlineFlex}
                            >
                              <span>
                                <IconButton className={classes.iconButton} disableRipple>
                                  <img src={DocumentIcon} alt="document" />
                                </IconButton>
                                <span className={classes.marginLeft}>Work Log PDF</span>
                              </span>
                              <IconButton className={classes.iconButton} disableRipple>
                                <img src={DownloadIcon} alt="download" />
                              </IconButton>
                            </Typography>
                          </div>
                        )}
                      </Grid>
                    )}
                  </Grid>
                {/* </Collapse> */}
                <CardActions
                  className={classes.actionsWrapper}
                  disableSpacing
                  onClick={handleExpandClick}
                >
                  <IconButton
                    className={classnames(classes.expand, {
                      [classes.expandOpen]: isExpanded,
                    })}
                    aria-expanded={isExpanded}
                    aria-label="Show more"
                    disableRipple
                    disableFocusRipple
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                  <Typography color="textSecondary">
                    {isExpanded ? 'Hide work log documents' : 'Show work log documents'}
                  </Typography>
                  <IconButton
                    className={classnames(classes.expand, {
                      [classes.expandOpen]: isExpanded,
                    })}
                    aria-expanded={isExpanded}
                    aria-label="Show more"
                    disableRipple
                    disableFocusRipple
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </CardActions>
              </Grid>
            </SwiperSlide>
          ))}
        </Swiper>
      <SignaturePopover
        anchorEl={anchorEl}
        handleClosePopover={handleClosePopover}
        signatures={signatures}
        placement="bottom-start"
        defaultPosition
      />
      </Card>

      {isModalOpen && (
        <ConfirmDialog
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={deleteSheet}
          text={`Are you sure you want to delete this submission (${submissionTime(submission)})? This action cannot be undone.`}
          disableEscape
          loadingOnSubmit
        />
      )}
    </>
  );
}

SubmittedSheetCard.propTypes = {
  classes: propTypes.object.isRequired,
  openSnackbar: propTypes.func.isRequired,
  setForceUpdate: propTypes.func.isRequired,
  // submission: propTypes.object.isRequired,
  sheet: propTypes.object.isRequired,
};

export default withStyles(styles)(SubmittedSheetCard);
